/**
 * Shared GraphQL queries for the SocialHood app
 * Centralized to avoid duplication and maintain consistency
 */

export const POST_QUERIES = {
  // Optimized list queries WITHOUT nested data (for fast feed loading)
  LIST_POSTS_FAST: `
    query ListPosts($limit: Int, $nextToken: String, $filter: ModelPostFilterInput, $sortDirection: ModelSortDirection) {
      listPosts(limit: $limit, nextToken: $nextToken, filter: $filter, sortDirection: $sortDirection) {
        items {
          id
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  `,

  LIST_POSTS_BY_TYPE_FAST: `
    query ListPostByTypeAndCreatedAt($type: String!, $createdAt: ModelStringKeyConditionInput, $limit: Int, $nextToken: String, $sortDirection: ModelSortDirection) {
      listPostByTypeAndCreatedAt(type: $type, createdAt: $createdAt, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection) {
        items {
          id
          type
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  `,

  LIST_POSTS_BY_NEIGHBORHOOD_FAST: `
    query ListPostByNeighborhoodAndCreatedAt($neighborhood: String!, $createdAt: ModelStringKeyConditionInput, $limit: Int, $nextToken: String, $sortDirection: ModelSortDirection) {
      listPostByNeighborhoodAndCreatedAt(neighborhood: $neighborhood, createdAt: $createdAt, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection) {
        items {
          id
          type
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  `,

  LIST_POSTS_BY_OWNER_FAST: `
    query ListPostByOwnerAndCreatedAt($owner: String!, $createdAt: ModelStringKeyConditionInput, $limit: Int, $nextToken: String, $sortDirection: ModelSortDirection) {
      listPostByOwnerAndCreatedAt(owner: $owner, createdAt: $createdAt, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection) {
        items {
          id
          type
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  `,

  // Separate query to fetch images for a specific post
  GET_POST_IMAGES: `
    query GetPostImages($postId: ID!) {
      getPost(id: $postId) {
        id
        images {
          items {
            id
            originalUrl
            mediumUrl
            thumbnailUrl
            order
          }
        }
      }
    }
  `,

  // Separate query to get comment count for a post
  GET_POST_COMMENT_COUNT: `
    query GetPostCommentCount($filter: ModelCommentFilterInput) {
      listComments(filter: $filter, limit: 1) {
        items {
          id
        }
      }
    }
  `,

  // Original queries with nested data (kept for single post view)
  LIST_POSTS: `
    query ListPosts($limit: Int, $nextToken: String, $filter: ModelPostFilterInput, $sortDirection: ModelSortDirection) {
      listPosts(limit: $limit, nextToken: $nextToken, filter: $filter, sortDirection: $sortDirection) {
        items {
          id
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
          images {
            items {
              id
              originalUrl
              mediumUrl
              thumbnailUrl
              order
            }
          }
          comments {
            items {
              id
            }
          }
        }
        nextToken
      }
    }
  `,

  // GSI query for main feed - sorted by type and createdAt
  LIST_POSTS_BY_TYPE: `
    query ListPostByTypeAndCreatedAt($type: String!, $createdAt: ModelStringKeyConditionInput, $limit: Int, $nextToken: String, $sortDirection: ModelSortDirection) {
      listPostByTypeAndCreatedAt(type: $type, createdAt: $createdAt, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection) {
        items {
          id
          type
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
          images {
            items {
              id
              originalUrl
              mediumUrl
              thumbnailUrl
              order
            }
          }
          comments {
            items {
              id
            }
          }
        }
        nextToken
      }
    }
  `,

  // GSI query for neighborhood feed - sorted by neighborhood and createdAt
  LIST_POSTS_BY_NEIGHBORHOOD: `
    query ListPostByNeighborhoodAndCreatedAt($neighborhood: String!, $createdAt: ModelStringKeyConditionInput, $limit: Int, $nextToken: String, $sortDirection: ModelSortDirection) {
      listPostByNeighborhoodAndCreatedAt(neighborhood: $neighborhood, createdAt: $createdAt, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection) {
        items {
          id
          type
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
          images {
            items {
              id
              originalUrl
              mediumUrl
              thumbnailUrl
              order
            }
          }
          comments {
            items {
              id
            }
          }
        }
        nextToken
      }
    }
  `,

  // GSI query for user feed - sorted by owner and createdAt
  LIST_POSTS_BY_OWNER: `
    query ListPostByOwnerAndCreatedAt($owner: String!, $createdAt: ModelStringKeyConditionInput, $limit: Int, $nextToken: String, $sortDirection: ModelSortDirection) {
      listPostByOwnerAndCreatedAt(owner: $owner, createdAt: $createdAt, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection) {
        items {
          id
          type
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          owner
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
          images {
            items {
              id
              originalUrl
              mediumUrl
              thumbnailUrl
              order
            }
          }
          comments {
            items {
              id
            }
          }
        }
        nextToken
      }
    }
  `,

  GET_POST: `
    query GetPost($id: ID!) {
      getPost(id: $id) {
        id
        title
        content
        author
        authorId
        authorProfilePic
        neighborhood
        owner
        upvoteCount
        downvoteCount
        createdAt
        updatedAt
        images {
          items {
            id
            originalUrl
            mediumUrl
            thumbnailUrl
            order
          }
        }
        comments {
          items {
            id
            content
            author
            authorId
            authorProfilePic
            createdAt
            updatedAt
          }
        }
      }
    }
  `
};

export const VOTE_QUERIES = {
  GET_USER_VOTE: `
    query GetUserVote($filter: ModelVoteFilterInput) {
      listVotes(filter: $filter) {
        items {
          id
          postId
          userId
          voteType
          owner
          createdAt
          updatedAt
        }
      }
    }
  `,

  LIST_VOTES: `
    query ListVotes($filter: ModelVoteFilterInput) {
      listVotes(filter: $filter) {
        items {
          id
          postId
          userId
          voteType
          createdAt
        }
      }
    }
  `
};

export const COMMENT_QUERIES = {
  LIST_COMMENTS: `
    query ListComments($filter: ModelCommentFilterInput) {
      listComments(filter: $filter) {
        items {
          id
          postId
          content
          author
          authorId
          authorProfilePic
          createdAt
          updatedAt
        }
      }
    }
  `,

  GET_COMMENT: `
    query GetComment($id: ID!) {
      getComment(id: $id) {
        id
        postId
        content
        author
        authorId
        authorProfilePic
        createdAt
        updatedAt
      }
    }
  `
};

export const USER_QUERIES = {
  GET_USER: `
    query GetUser($id: ID!) {
      getUser(id: $id) {
        id
        username
        email
        profilePicture
        firstName
        lastName
        createdAt
        updatedAt
      }
    }
  `
};